// Utilitário para gerenciar cookies de afiliados
import React from 'react';

/**
 * Configurações padrão para cookies de afiliado
 */
const DEFAULT_CONFIG = {
  duration: 30, // dias
  cookieName: 'pagmus_affiliate',
  domain: window.location.hostname,
  secure: window.location.protocol === 'https:',
  sameSite: 'lax'
};

/**
 * Define um cookie com configurações específicas
 * @param {string} name - Nome do cookie
 * @param {string} value - Valor do cookie
 * @param {number} days - Duração em dias
 * @param {Object} options - Opções adicionais
 */
function setCookie(name, value, days, options = {}) {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  let cookieString = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/`;
  
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }
  
  if (options.secure) {
    cookieString += `; secure`;
  }
  
  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  }
  
  document.cookie = cookieString;
}

/**
 * Obtém o valor de um cookie
 * @param {string} name - Nome do cookie
 * @returns {string|null} Valor do cookie ou null se não encontrado
 */
function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
}

/**
 * Remove um cookie
 * @param {string} name - Nome do cookie
 */
function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Detecta parâmetros de afiliado na URL atual
 * Distingue entre affiliate_code (começa com aff_) e link_code (campanha)
 * @returns {Object|null} Dados do afiliado ou null se não encontrado
 */
function detectAffiliateFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');

  // Parâmetros possíveis para identificar afiliados
  const affiliateParams = [
    'affiliate_id', 'ref', 'referrer', 'aff', 'partner',
    'utm_source', 'utm_campaign', 'utm_medium', 'mkt'
  ];

  const affiliateData = {};
  let hasAffiliateData = false;

  // Verificar parâmetros na URL
  affiliateParams.forEach(param => {
    const value = urlParams.get(param) || hashParams.get(param);
    if (value) {
      affiliateData[param] = value;
      hasAffiliateData = true;
    }
  });

  // Processar parâmetro aff especificamente
  // Distinguir entre affiliate_code (aff_xxx) e link_code (campanha)
  if (affiliateData.aff) {
    if (affiliateData.aff.startsWith('aff_')) {
      // É um affiliate_code (código direto do afiliado)
      affiliateData.affiliate_code = affiliateData.aff;
      affiliateData.link_code = null;
    } else {
      // É um link_code (código de campanha)
      affiliateData.affiliate_code = null;
      affiliateData.link_code = affiliateData.aff;
    }
  }

  // Processar parâmetro mkt (código de campanha/marketing)
  if (affiliateData.mkt) {
    affiliateData.campaign_code = affiliateData.mkt;
  }

  // Adicionar timestamp e URL de origem
  if (hasAffiliateData) {
    affiliateData.timestamp = Date.now();
    affiliateData.source_url = window.location.href;
    affiliateData.user_agent = navigator.userAgent;

    return affiliateData;
  }

  return null;
}

/**
 * Salva dados de afiliado em cookie
 * Inclui campos específicos para affiliate_code e link_code para compatibilidade com backend
 *
 * @param {Object} affiliateData - Dados do afiliado
 * @param {number} durationDays - Duração em dias (padrão: 30)
 * @returns {boolean} Sucesso da operação
 */
function saveAffiliateCookie(affiliateData, durationDays = DEFAULT_CONFIG.duration) {
  try {
    const cookieData = {
      ...affiliateData,
      // Garantir que affiliate_code e link_code estão presentes para o backend
      affiliate_code: affiliateData.affiliate_code || null,
      link_code: affiliateData.link_code || null,
      code: affiliateData.affiliate_code || affiliateData.link_code || affiliateData.aff || null, // Backward compatibility
      duration_hours: durationDays * 24,
      saved_at: Date.now(),
      expires_at: Date.now() + (durationDays * 24 * 60 * 60 * 1000)
    };

    const cookieValue = JSON.stringify(cookieData);

    setCookie(
      DEFAULT_CONFIG.cookieName,
      cookieValue,
      durationDays,
      {
        domain: DEFAULT_CONFIG.domain,
        secure: DEFAULT_CONFIG.secure,
        sameSite: DEFAULT_CONFIG.sameSite
      }
    );

    console.log('🍪 Cookie de afiliado salvo:', {
      duration: durationDays,
      affiliate_code: cookieData.affiliate_code,
      link_code: cookieData.link_code,
      data: affiliateData
    });

    return true;
  } catch (error) {
    console.error('❌ Erro ao salvar cookie de afiliado:', error);
    return false;
  }
}

/**
 * Recupera dados de afiliado do cookie
 * @returns {Object|null} Dados do afiliado ou null se não encontrado/expirado
 */
function getAffiliateCookie() {
  try {
    const cookieValue = getCookie(DEFAULT_CONFIG.cookieName);
    
    if (!cookieValue) {
      return null;
    }
    
    const cookieData = JSON.parse(cookieValue);
    
    // Verificar se o cookie expirou
    if (cookieData.expires_at && Date.now() > cookieData.expires_at) {
      console.log('🍪 Cookie de afiliado expirado, removendo...');
      deleteCookie(DEFAULT_CONFIG.cookieName);
      return null;
    }
    
    console.log('🍪 Cookie de afiliado encontrado:', cookieData);
    return cookieData;
    
  } catch (error) {
    console.error('❌ Erro ao ler cookie de afiliado:', error);
    // Remover cookie corrompido
    deleteCookie(DEFAULT_CONFIG.cookieName);
    return null;
  }
}

/**
 * Processa link de afiliado completo (detecção + salvamento)
 * @param {number} durationDays - Duração do cookie em dias
 * @returns {Object|null} Dados do afiliado processado
 */
function processAffiliateLink(durationDays = DEFAULT_CONFIG.duration) {
  // Primeiro, verificar se já existe cookie válido
  const existingAffiliate = getAffiliateCookie();
  
  // Detectar novos dados de afiliado na URL
  const newAffiliateData = detectAffiliateFromUrl();
  
  if (newAffiliateData) {
    // Sempre salvar novos dados de afiliado (sobrescreve anteriores)
    saveAffiliateCookie(newAffiliateData, durationDays);
    console.log('🔗 Novo link de afiliado processado');
    return newAffiliateData;
  } else if (existingAffiliate) {
    // Usar dados existentes se não há novos dados na URL
    console.log('🔗 Usando dados de afiliado existentes do cookie');
    return existingAffiliate;
  }
  
  console.log('🔗 Nenhum dados de afiliado encontrado');
  return null;
}

/**
 * Salva cookie de afiliado no formato específico por produto (para compatibilidade com backend)
 * Suporta tanto affiliate_code (afiliado direto) quanto link_code (campanha)
 *
 * @param {string} linkCode - Código do link de campanha OU affiliate_code
 * @param {number} productId - ID do produto
 * @param {number} durationHours - Duração em horas (padrão: 168 = 7 dias)
 * @param {number} affiliateId - ID do afiliado (opcional)
 * @returns {boolean} Sucesso da operação
 */
function saveAffiliateProductCookie(linkCode, productId, durationHours = 168, affiliateId = null) {
  try {
    const cookieName = `aff_${productId}`;
    const durationDays = durationHours / 24;

    // Detectar se é affiliate_code (começa com aff_) ou link_code (campanha)
    const isAffiliateCode = linkCode && linkCode.startsWith('aff_');

    const cookieData = {
      code: linkCode,                                    // Backward compatibility
      affiliate_code: isAffiliateCode ? linkCode : null, // Novo: código do afiliado
      link_code: isAffiliateCode ? null : linkCode,      // Código de campanha
      affiliate_id: affiliateId,                         // ID do afiliado
      timestamp: Date.now(),
      duration_hours: durationHours,
      expires_at: Date.now() + (durationHours * 60 * 60 * 1000)
    };

    const cookieValue = JSON.stringify(cookieData);

    setCookie(
      cookieName,
      cookieValue,
      durationDays,
      {
        domain: DEFAULT_CONFIG.domain,
        secure: DEFAULT_CONFIG.secure,
        sameSite: DEFAULT_CONFIG.sameSite
      }
    );

    console.log(`🍪 Cookie de afiliado salvo: ${cookieName}`, {
      affiliate_code: cookieData.affiliate_code,
      link_code: cookieData.link_code,
      affiliate_id: affiliateId,
      duration: `${durationHours}h`,
      expires_at: new Date(cookieData.expires_at).toLocaleString()
    });

    return true;
  } catch (error) {
    console.error('❌ Erro ao salvar cookie de afiliado do produto:', error);
    return false;
  }
}

/**
 * Limpa todos os dados de afiliado
 */
function clearAffiliate() {
  deleteCookie(DEFAULT_CONFIG.cookieName);
  console.log('🗑️ Dados de afiliado removidos');
}

/**
 * Debug: mostra informações sobre cookies de afiliado
 */
function debugAffiliateCookies() {
  const urlAffiliateData = detectAffiliateFromUrl();
  const cookieAffiliateData = getAffiliateCookie();
  
  console.group('🔍 Debug Afiliado');
  console.log('URL atual:', window.location.href);
  console.log('Dados na URL:', urlAffiliateData);
  console.log('Dados no cookie:', cookieAffiliateData);
  console.log('Cookie bruto:', getCookie(DEFAULT_CONFIG.cookieName));
  console.groupEnd();
  
  return {
    url: urlAffiliateData,
    cookie: cookieAffiliateData,
    rawCookie: getCookie(DEFAULT_CONFIG.cookieName)
  };
}

/**
 * Hook personalizado para usar sistema de afiliados
 * @returns {Object} Métodos e dados do sistema de afiliados
 */
function useAffiliateDetection() {
  const [affiliateData, setAffiliateData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    try {
      const processedData = processAffiliateLink();
      setAffiliateData(processedData);
    } catch (error) {
      console.error('❌ Erro no useAffiliateDetection:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const checkForAffiliate = React.useCallback((durationDays) => {
    const data = processAffiliateLink(durationDays);
    setAffiliateData(data);
    return data;
  }, []);
  
  const clearAffiliateCallback = React.useCallback(() => {
    clearAffiliate();
    setAffiliateData(null);
  }, []);
  
  return {
    affiliateData,
    loading,
    checkForAffiliate,
    clearAffiliate: clearAffiliateCallback,
    debugAffiliateCookies
  };
}

export {
  setCookie,
  getCookie,
  deleteCookie,
  detectAffiliateFromUrl,
  saveAffiliateCookie,
  saveAffiliateProductCookie,  // ✅ NOVO: Para compatibilidade com backend
  getAffiliateCookie,
  processAffiliateLink,
  clearAffiliate,
  debugAffiliateCookies,
  useAffiliateDetection,
  DEFAULT_CONFIG
};
